import DOMPurify from 'dompurify'

import {FIXED, FUNCTION, RAINBOW, SENSOR, VALUE} from '../consts'
import {ArgumentType, FunctionDefinition} from '../types'

export function escapeHtml(str: string) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function highlightFunctions(str: string, functions: FunctionDefinition[]): string {
  const sortedFunctions = [...functions].sort((a, b) => b.name.length - a.name.length)
  let result = str

  for (const fn of sortedFunctions) {
    const regex = new RegExp(`\\b${fn.name}(\\s*)(\\(([^)]*)?)?`, 'g')

    result = result.replace(regex, (_, space: string, bracketGroup: string, argsStr: string) => {
      const highlightedName = `<span style="color:${FUNCTION}">${fn.name}</span>`

      if (!bracketGroup) {
        return highlightedName
      }

      const args = argsStr ? splitArgs(argsStr) : []
      const highlightedArgs = args.map((arg, index) => {
        const argDef = fn.arguments[index]

        const plain = arg.replace(/<[^>]+>/g, '').trim()
        const startsWithFunction = functions.some(({name}) =>
          new RegExp(`^${name}\\s*\\(`).test(plain)
        )

        if (startsWithFunction) {
          return `<span style="color:${FUNCTION}">${arg}</span>`
        }

        const isQuoted = /^'.*'$/.test(plain) || /^".*"$/.test(plain)
        const simpleLiteral = isQuoted || /^[^+\-*/()]+$/.test(plain)
        if (!simpleLiteral) return arg

        if (argDef?.type === ArgumentType.Fix) return `<span style="color:${FIXED}">${arg}</span>`
        if (argDef?.type === ArgumentType.Sensor)
          return `<span style="color:${SENSOR}">${arg}</span>`
        if (argDef?.type === ArgumentType.Val) return `<span style="color:${VALUE}">${arg}</span>`
        return arg
      })

      let rebuilt = ''
      let lastIndex = 0

      args.forEach((arg, i) => {
        const index = argsStr.indexOf(arg, lastIndex)
        rebuilt += argsStr.slice(lastIndex, index) + highlightedArgs[i]
        lastIndex = index + arg.length
      })

      if (argsStr) rebuilt += argsStr.slice(lastIndex)

      return `${highlightedName}${space}(${rebuilt}`
    })
  }

  return result
}

export function splitArgs(str: string): string[] {
  const args: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < str.length; i++) {
    const char = str[i]
    if (char === "'" || char === '"') {
      inQuotes = !inQuotes
      current += char
    } else if (char === ',' && !inQuotes) {
      args.push(current)
      current = ''
    } else {
      current += char
    }
  }

  if (current) args.push(current)
  return args
}

const sanitize = (html: string) =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['span', 'br'],
    ALLOWED_ATTR: ['style']
  })

export function highlightOperatorsAndBrackets(
  value: string,
  functions?: FunctionDefinition[]
): string {
  const text = functions ? highlightFunctions(value, functions) : value

  let result = ''
  const stack: string[] = []

  let i = 0
  while (i < text.length) {
    if (text[i] === '<') {
      const end = text.indexOf('>', i)
      if (end === -1) {
        result += text.slice(i)
        break
      }
      result += text.slice(i, end + 1)
      i = end + 1
      continue
    }

    const ch = text[i]

    if (ch === '(') {
      const color = RAINBOW[stack.length % RAINBOW.length]
      stack.push(color)
      result += `<span style="color:${color}">(</span>`
    } else if (ch === ')') {
      const color = stack.pop() || 'black'
      result += `<span style="color:${color}">)</span>`
    } else {
      result += escapeHtml(ch)
    }

    i++
  }

  return sanitize(result)
}
